import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useLang } from "@/LanguageContext";
import t from "@/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLAGS_CACHE_KEY = "vmbs_header_flags_v1";

// Read cached flags synchronously on first render — no menu pop-in on subpage navigation
function readCachedFlags() {
  try {
    // Inline script in index.html may have already populated this from /api/header-flags
    if (typeof window !== "undefined" && window.__VMBS_HEADER_FLAGS__) {
      return { ...window.__VMBS_HEADER_FLAGS__, _hasCache: true };
    }
    const raw = sessionStorage.getItem(FLAGS_CACHE_KEY);
    if (!raw) return { karriere: false, events: false, newsletter: false, _hasCache: false };
    const parsed = JSON.parse(raw);
    return { ...parsed, _hasCache: true };
  } catch {
    return { karriere: false, events: false, newsletter: false, _hasCache: false };
  }
}

// Flag SVG components
function FlagDE() {
  return (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="5.33" fill="#000" />
      <rect y="5.33" width="24" height="5.33" fill="#DD0000" />
      <rect y="10.66" width="24" height="5.34" fill="#FFCE00" />
    </svg>
  );
}

function FlagGB() {
  return (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="16" fill="#012169" />
      <path d="M0,0 L24,16 M24,0 L0,16" stroke="#fff" strokeWidth="2.5" />
      <path d="M0,0 L24,16 M24,0 L0,16" stroke="#C8102E" strokeWidth="1.5" />
      <path d="M12,0 V16 M0,8 H24" stroke="#fff" strokeWidth="4" />
      <path d="M12,0 V16 M0,8 H24" stroke="#C8102E" strokeWidth="2.5" />
    </svg>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const initialFlags = readCachedFlags();
  const [showKarriere, setShowKarriere] = useState(initialFlags.karriere);
  const [showEvents, setShowEvents] = useState(initialFlags.events);
  const [showNewsletter, setShowNewsletter] = useState(initialFlags.newsletter);
  const { lang, toggle } = useLang();
  const tx = t[lang];

  useEffect(() => {
    // Skip network call if we already have a fresh session cache — menu shows instantly
    if (initialFlags._hasCache) return;
    // First, listen for the inline-script's flag-ready event (HTML preload may finish before React mounts)
    const onReady = () => {
      const d = window.__VMBS_HEADER_FLAGS__;
      if (d) {
        setShowKarriere(!!d.karriere);
        setShowEvents(!!d.events);
        setShowNewsletter(!!d.newsletter);
      }
    };
    window.addEventListener("vmbs:flags-ready", onReady);
    // Also poll once in case the event already fired before this listener attached
    onReady();
    // Fallback for browsers without the inline script (or if it failed)
    axios.get(`${API}/header-flags`).then(({ data }) => {
      setShowKarriere(!!data.karriere);
      setShowEvents(!!data.events);
      setShowNewsletter(!!data.newsletter);
      try { sessionStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(data)); } catch {}
    }).catch(() => {});
    return () => window.removeEventListener("vmbs:flags-ready", onReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navLinks = [
    { label: tx.nav_argumente, href: "#argumente" },
    ...(showEvents ? [{ label: tx.nav_veranstaltungen, href: "#veranstaltungen" }] : []),
    { label: tx.nav_kontakt, href: "#kontakt" },
    ...(showNewsletter ? [{ label: tx.nav_newsletter, href: "#newsletter" }] : []),
    ...(showKarriere ? [{ label: tx.nav_karriere, href: "/karriere", isPage: true }] : []),
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      window.location.href = href;
      return;
    }
    // If on a subpage (not landing page), navigate to landing page with hash
    if (window.location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0C1620]/80 backdrop-blur-xl border-b border-[#3A4F60]/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
        <Link
          to="/"
          className="flex items-center gap-2 sm:gap-3 flex-shrink-0"
          data-testid="logo-link"
          onClick={(e) => {
            if (window.location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <img src="/images/vmbs-logo.png" alt="VMBS UG Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" data-testid="vmbs-logo" />
          <span className="font-heading text-lg sm:text-xl tracking-widest text-white font-semibold hidden sm:inline">
            V<span className="text-[#C5993A]">·</span>M<span className="text-[#C5993A]">·</span>B<span className="text-[#C5993A]">·</span>S
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="text-xs xl:text-sm uppercase tracking-[0.12em] text-[#8899AA] hover:text-[#C5993A] transition-colors duration-300"
            >
              {link.label}
            </button>
          ))}

          {/* Language toggle - show opposite flag */}
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="p-1.5 rounded hover:bg-[#3A4F60]/30 transition-colors"
            title={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
          >
            {lang === "de" ? <FlagGB /> : <FlagDE />}
          </button>

          <Button
            data-testid="header-cta-button"
            onClick={() => scrollTo("#kontakt")}
            className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium px-4 xl:px-5 py-2 text-sm rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0"
          >
            {tx.header_cta}
          </Button>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          {/* Mobile language toggle */}
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggle}
            className="p-1.5 rounded hover:bg-[#3A4F60]/30 transition-colors"
          >
            {lang === "de" ? <FlagGB /> : <FlagDE />}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#F0F4F8] p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#12202f] border-t border-[#3A4F60]/30 px-4 sm:px-6 py-4 space-y-3">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="block w-full text-left text-sm uppercase tracking-[0.12em] text-[#8899AA] hover:text-[#C5993A] transition-colors py-2"
            >
              {link.label}
            </button>
          ))}
          <Button onClick={() => scrollTo("#kontakt")} className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
            {tx.header_cta}
          </Button>
        </div>
      )}
    </header>
  );
}
